/**
 * Showroom Token Resolver
 *
 * Используется когда лендинг / print-template открыт в standalone-режиме
 * (не как widget на клиентском сайте) и получает ?token=xxx в URL.
 *
 * Flow:
 *   1. Читаем ?token из URL
 *   2. GET /api/public/widget/config?token=xxx — public endpoint, без auth
 *   3. Наполняем window.b2bMountParams resolved organizationId/inventorySlug
 *      — дальше config getters в runtimeConfig.js подхватят их автоматически
 *   4. Применяем client theme на :root (document.documentElement) через setProperty
 *
 * Все ошибки ловятся — при отсутствии/невалидности токена возвращаем дефолты,
 * приложение продолжает рендер с fallback inventory (org=1, slug=demo-autopolis).
 */

import { loadWidgetConfig, mapApiConfigToMountConfig } from './widgetConfigLoader';
import { getUrlParam } from '../utils/urlParams';

// Жёсткий cap на резолв токена. Превышение → fallback на defaults и render
// продолжается. Подобран так, чтобы покрыть холодный старт API в облаке,
// но не оставить пользователя на белом экране слишком долго.
const RESOLVE_TIMEOUT_MS = 5000;

// mapping mountConfig.theme.* → CSS custom properties на :root.
// Совпадает с THEME_MAPPING в widget.jsx:152, но применяется к documentElement
// (standalone), а не к shadow root.
const THEME_TO_CSS_VAR = {
  primaryColor: '--theme-primary',
  primaryHover: '--theme-primary-hover',
  accentColor: '--theme-accent',
  bgColor: '--theme-bg-primary',
  textColor: '--theme-text-primary',
  fontFamily: '--theme-font-family',
  fontSize: '--theme-font-size-base',
  borderRadius: '--radius',
  cardRadius: '--theme-card-radius',
  buttonRadius: '--theme-button-radius',
  spacingSm: '--theme-spacing-sm',
  spacingMd: '--theme-spacing-md',
  spacingLg: '--theme-spacing-lg',
};

function applyThemeToRoot(theme) {
  if (!theme || typeof theme !== 'object') return;
  const root = document.documentElement;
  let applied = 0;
  for (const [themeKey, value] of Object.entries(theme)) {
    const cssVar = THEME_TO_CSS_VAR[themeKey];
    if (cssVar && value) {
      root.style.setProperty(cssVar, value);
      applied += 1;
    }
  }
  console.debug(`[ShowroomTokenResolver] Applied ${applied} theme vars to :root`);
}

/**
 * Resolve ?token=xxx из URL в organization/inventory/theme.
 * Вызывается в main.jsx ДО рендера React-приложения.
 *
 * Если токена в URL нет — no-op, приложение стартует с дефолтами.
 * Если токен невалиден или API упал — логируем, но не блокируем рендер.
 *
 * @returns {Promise<void>}
 */
export async function resolveShowroomToken() {
  const token = getUrlParam('token');
  if (!token) return;

  // apiUrl может быть задан через env или runtime config; fallback на same-origin.
  const runtimeApiUrl =
    (typeof window !== 'undefined' && window.runtimeConfig?.VITE_CAR_API_URL) ||
    import.meta.env.VITE_CAR_API_URL ||
    '';

  // Promise.race с timeout — даже если backend холодный/завис, рендер
  // получит управление через RESOLVE_TIMEOUT_MS и приложение запустится
  // с дефолтным inventory. timeoutId сохраняем чтобы очистить таймер
  // на ранний exit (избегаем висящих setTimeout-ов в фоне).
  let timeoutId;
  const timeout = new Promise((resolve) => {
    timeoutId = setTimeout(() => resolve(null), RESOLVE_TIMEOUT_MS);
  });

  try {
    const apiConfig = await Promise.race([loadWidgetConfig(runtimeApiUrl, token), timeout]);
    if (!apiConfig) {
      console.warn('[ShowroomTokenResolver] loadWidgetConfig timed out or returned null, falling back to defaults');
      return;
    }

    const mountConfig = mapApiConfigToMountConfig(apiConfig, runtimeApiUrl, token);

    // Положить весь mountConfig в window.b2bMountParams — config getters в
    // runtimeConfig.js прочитают его как первый приоритет.
    //
    // Spread всего объекта (а не cherry-pick whitelist) — единый source of
    // truth: что mapApiConfigToMountConfig вернул, то и видит runtime. Раньше
    // whitelist приходилось вручную расширять при каждом новом поле
    // (workflow/agentId, theme, dealerLogoUrl, suggestions*, enableShowroomCatalog —
    // последний был добавлен в widgetConfigLoader для #965, но забыт здесь →
    // на test/prod standalone без VITE_ENABLE_SHOWROOM_CATALOG env-fallback
    // фича молча выключалась). Тот же паттерн что у bootstrap.jsx populateLegacyConfig.
    //
    // ВАЖНО: existing mountParams (если что-то заинлайнено в index.html до
    // main.jsx) перетираем — токен в URL = explicit user intent, побеждает.
    window.b2bMountParams = {
      ...(window.b2bMountParams || {}),
      ...mountConfig,
    };

    // Применить theme до первого рендера — избежит flash-of-default-theme.
    applyThemeToRoot(mountConfig.theme);

    console.debug('[ShowroomTokenResolver] Resolved token →', {
      organizationId: mountConfig.organizationId,
      inventorySlug: mountConfig.inventorySlug,
      workflow: mountConfig.workflow,
      agentId: mountConfig.agentId,
      hasTheme: !!mountConfig.theme,
      widgetStyle: mountConfig.widgetStyle,
      widgetThemeMode: mountConfig.widgetThemeMode,
    });
  } catch (err) {
    // Не блокируем рендер приложения при ошибке резолва — получим fallback inventory
    console.error('[ShowroomTokenResolver] Failed to resolve token:', err);
  } finally {
    clearTimeout(timeoutId);
  }
}
